// Payables: vendor bills from entry to payment. A bill is checked three ways against its PO and
// what was received; approving it posts it; paying it posts the payment. Receipts for parts bought
// on jobs wait beside the bills (BL-012), and returns to vendors wait for their credit (BL-013).
// Purchase orders over the approval limit wait for the owner.

import { useState } from 'react';
import { holdsAny } from '../shared/may.ts';
import { useDesign } from '../blueprint/design.ts';
import { newId } from '../shared/ids.ts';
import type { BooksProps } from './props.ts';
import { BUCKETS, BUCKET_LABEL, dayText, todayIn } from './model.ts';
import type { ApAging, BankAccount, PurchaseOrder, VendorBill } from './types.ts';
import {
  ClosedMonthHint,
  Empty,
  Loading,
  Money,
  Panel,
  RowActions,
  useAct,
  useQuery,
  useRowPrimary,
} from './ui.tsx';
import { AP_PERMS, EnterBill, VendorCredits, VendorsPanel } from './PayablesEntry.tsx';
import { ReceiptsToApprove } from './Receipts.tsx';
import { ReturnsAwaiting } from './ReturnsAwaiting.tsx';

function PayForm({
  bill,
  banks,
  act,
  onDone,
}: {
  bill: { billId: string; vendorId: string; owingCents: number };
  banks: BankAccount[];
  act: ReturnType<typeof useAct>;
  onDone: () => void;
}) {
  const [bankAccountId, setBank] = useState(banks[0]?.id ?? '');
  const [method, setMethod] = useState<'eft' | 'cheque' | 'card' | 'pad' | 'etransfer'>('eft');
  const [paidOn, setPaidOn] = useState(todayIn());
  const [reference, setReference] = useState('');
  return (
    <div className="fgrid inline-form">
      <label>
        <span className="flabel">From</span>
        <select className="field" value={bankAccountId} onChange={(e) => setBank(e.target.value)}>
          {banks.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className="flabel">How</span>
        <select className="field" value={method} onChange={(e) => setMethod(e.target.value as typeof method)}>
          <option value="eft">EFT</option>
          <option value="cheque">Cheque</option>
          <option value="pad">Pre-authorised debit</option>
          <option value="etransfer">E-transfer</option>
          <option value="card">Card</option>
        </select>
      </label>
      <label>
        <span className="flabel">Paid on</span>
        <input className="field" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
        <ClosedMonthHint date={paidOn} />
      </label>
      <label>
        <span className="flabel">Reference</span>
        <input
          className="field"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="Optional"
        />
      </label>
      <div className="row-actions">
        <button
          className="btn primary"
          disabled={act.busy || !bankAccountId}
          onClick={() =>
            void (async () => {
              const ok = await act.run(
                'vendorBills.pay',
                {
                  paymentId: newId(),
                  vendorId: bill.vendorId,
                  bankAccountId,
                  paidOn,
                  method,
                  ...(reference ? { reference } : {}),
                  allocations: [{ billId: bill.billId, amountCents: bill.owingCents }],
                },
                'Paid and posted.',
              );
              if (ok) onDone();
            })()
          }
        >
          Pay <Money cents={bill.owingCents} />
        </button>
        <button className="btn quiet" onClick={onDone}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** Who orders (purchasing.order): the owner, the manager and the warehouse. */
const PO_PERMS = ['purchasing.order'];

export function Payables({ held: may, toast, timeZone }: BooksProps) {
  const aging = useQuery<ApAging>('payables.aging', {});
  const drafts = useQuery<{ bills: VendorBill[] }>('vendorBills.list', { status: 'draft' });
  const held = useQuery<{ bills: VendorBill[] }>('vendorBills.list', { status: 'on_hold' });
  const banks = useQuery<{ bankAccounts: BankAccount[] }>('bankAccounts.list', {});
  // Purchase orders are for those who order (purchasing.order): asked only of them (QA-260).
  const ordersPos = holdsAny(may, PO_PERMS);
  const pos = useQuery<{ orders: PurchaseOrder[] }>('purchaseOrders.list', ordersPos ? {} : null);
  // Entering bills and vendors (gap #9): a new one reloads the lists it lands in, and an approval
  // or a payment reloads the vendor credits too (version).
  const [version, setVersion] = useState(0);
  const rowPrimary = useRowPrimary();
  const blueprint = useDesign() === 'blueprint';
  const act = useAct(toast, async () => {
    setVersion((v) => v + 1);
    await Promise.all([aging.reload(), drafts.reload(), held.reload(), pos.reload()]);
  });
  const [paying, setPaying] = useState<string | null>(null);
  // approving a purchase order (purchaseOrders.approve): purchasing.approve, the owner's or a role of theirs
  const isOwner = may.includes('purchasing.approve');
  const enters = holdsAny(may, AP_PERMS);
  const changed = () => {
    setVersion((v) => v + 1);
    void Promise.all([aging.reload(), drafts.reload()]);
  };

  if (!aging.data) return <Loading error={aging.error} what="the bills" retry={() => void aging.reload()} />;
  const waitingPos = (pos.data?.orders ?? []).filter((o) => o.needsOwnerApproval && o.status === 'draft');
  const ages = BUCKETS.map((b) => (
    <div key={b} className="aging-col">
      <span className="muted small">{BUCKET_LABEL[b]}</span>
      <Money cents={aging.data!.buckets[b]} className="mid" />
    </div>
  ));
  return (
    <div className="books-page">
      {enters && <EnterBill toast={toast} timeZone={timeZone} onChanged={changed} />}
      {/* in Blueprint the ages are the page's facts strip, not a section of their own */}
      {blueprint ? (
        <div className="aging" role="group" aria-label="Aging">
          {ages}
        </div>
      ) : (
        <Panel title="Aging">
          <div className="aging">{ages}</div>
        </Panel>
      )}

      <Panel
        title="To approve"
        aside={drafts.data ? <span className="chip">{drafts.data.bills.length}</span> : undefined}
      >
        {!drafts.data ? (
          <Loading error={drafts.error} what="the bills to approve" retry={() => void drafts.reload()} />
        ) : drafts.data.bills.length === 0 ? (
          <Empty>No bill is waiting for approval.</Empty>
        ) : (
          <ul className="cards">
            {drafts.data.bills.map((b) => {
              const variance = b.match.some((m) => m.priceVarianceCents !== 0);
              const short = b.match.some((m) => m.heldQty !== '0' && m.heldQty !== '0.000');
              return (
                <li key={b.id} className="stone line-card">
                  <div className="line-top">
                    <div>
                      <strong>
                        {b.number} · {b.vendorName}
                      </strong>
                      <span className="muted small">
                        {b.vendorRef} · billed {dayText(b.billDate)} · due {dayText(b.dueDate)}
                      </span>
                    </div>
                    <Money cents={b.totalCents} className="amount" />
                  </div>
                  {b.poId && (
                    <p className="small">
                      {short ? (
                        <span className="chip say warn">Not all received yet: that part waits</span>
                      ) : variance ? (
                        <span className="chip say warn">Price differs from the PO</span>
                      ) : (
                        <span className="chip say ok">3-way match</span>
                      )}
                    </p>
                  )}
                  <RowActions>
                    <button
                      className={rowPrimary}
                      disabled={act.busy}
                      onClick={() =>
                        void (b.kind === 'credit'
                          ? act.run('vendorBills.approveCredit', { creditId: b.id }, 'Approved.')
                          : act.run(
                              'vendorBills.approve',
                              { billId: b.id, acceptPriceVariance: variance },
                              'Approved and posted.',
                            ))
                      }
                    >
                      {variance ? 'Approve with the price difference' : 'Approve'}
                    </button>
                    <button
                      className="btn quiet"
                      disabled={act.busy}
                      onClick={() =>
                        void act.run(
                          'vendorBills.setHold',
                          { billId: b.id, on: true, reason: 'Held from the books' },
                          'On hold.',
                        )
                      }
                    >
                      Hold
                    </button>
                  </RowActions>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      {/* parts techs bought on jobs (BL-012): approved into AP or onto a company card */}
      {enters && <ReceiptsToApprove toast={toast} onChanged={changed} />}

      <Panel title="To pay" aside={<span className="chip">{aging.data.rows.length}</span>}>
        {aging.data.rows.length === 0 ? (
          <Empty>Nothing approved is unpaid.</Empty>
        ) : (
          <ul className="rows">
            {aging.data.rows.map((r) => (
              <li key={r.billId} className="inv-row">
                <div className="inv-main">
                  <div>
                    <strong>{r.number}</strong> <span>{r.vendorName}</span>
                    {r.daysPastDue > 0 && (
                      <span className="chip say bad">
                        {r.daysPastDue} day{r.daysPastDue === 1 ? '' : 's'} overdue
                      </span>
                    )}
                  </div>
                  <span className="muted small">Due {dayText(r.dueDate)}</span>
                </div>
                <Money cents={r.owingCents} className="amount" />
                <div className="row-actions">
                  <button className="btn" onClick={() => setPaying(r.billId)}>
                    Pay
                  </button>
                </div>
                {paying === r.billId && banks.data && (
                  <PayForm
                    bill={r}
                    banks={banks.data.bankAccounts}
                    act={act}
                    onDone={() => setPaying(null)}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {enters && <VendorCredits toast={toast} version={version} onChanged={changed} />}

      {/* parts sent back to vendors (BL-013): their credit entered from the return, or the return closed */}
      {enters && (
        <ReturnsAwaiting
          toast={toast}
          {...(timeZone ? { timeZone } : {})}
          version={version}
          onChanged={changed}
        />
      )}

      {(held.data?.bills.length ?? 0) > 0 && (
        <Panel title="On hold">
          <ul className="rows">
            {held.data!.bills.map((b) => (
              <li key={b.id} className="inv-row">
                <div className="inv-main">
                  <strong>
                    {b.number} · {b.vendorName}
                  </strong>
                  <span className="muted small">{b.holdReason ?? 'On hold'}</span>
                </div>
                <Money cents={b.totalCents} className="amount" />
                <div className="row-actions">
                  <button
                    className="btn"
                    disabled={act.busy}
                    onClick={() =>
                      void act.run('vendorBills.setHold', { billId: b.id, on: false }, 'Released.')
                    }
                  >
                    Release
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {ordersPos && (
        <Panel
          title="Purchase orders over the limit"
          aside={<span className="chip">{waitingPos.length}</span>}
        >
          {waitingPos.length === 0 ? (
            <Empty>No purchase order is waiting for the owner.</Empty>
          ) : (
            <ul className="rows">
              {waitingPos.map((o) => (
                <li key={o.id} className="inv-row">
                  <div className="inv-main">
                    <strong>{o.number}</strong>
                    <span className="muted small">
                      {o.auto ? 'Drafted by the weekly reorder' : 'Entered by the warehouse'}
                    </span>
                  </div>
                  <Money cents={o.totalCents} className="amount" />
                  <div className="row-actions">
                    {isOwner ? (
                      <button
                        className={rowPrimary}
                        disabled={act.busy}
                        onClick={() =>
                          void act.run(
                            'purchaseOrders.approve',
                            { poId: o.id },
                            'Approved: the warehouse can send it.',
                          )
                        }
                      >
                        Approve
                      </button>
                    ) : (
                      <span className="muted small">Waiting for the owner</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      )}

      {enters && <VendorsPanel toast={toast} onChanged={changed} />}
    </div>
  );
}
